import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { EulaDialogData } from '@models';
import { EulaService, StyleUtilService, UserDataService, UserSessionApiService } from '@services';
import { WINDOW } from 'core/window.token';
import { finalize, fromEvent, throttleTime } from 'rxjs';

@Component({
  selector: 'app-eula-dialog',
  templateUrl: './eula-dialog.component.html',
  styleUrls: ['./eula-dialog.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [CommonModule, MatDialogContent, MatDialogActions, MatIconModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EulaDialogComponent implements AfterViewInit, AfterViewChecked {
  reviewOnly = false;
  @ViewChild('eulaContent', { static: false }) eulaContentRef!: ElementRef<HTMLElement>;
  @Output() signedOut = new EventEmitter<void>();

  sanitizedContent: SafeHtml;
  title: string;
  isSavingEula = false;
  eulaId: number;
  eulaVersion: string;
  usePostMethod = false;
  hasReadFullDocument = false;
  acceptedAt: string | null = null;
  private notificationStylesApplied = false;

  private readonly dialogRef = inject(MatDialogRef<EulaDialogComponent>);
  private readonly data = inject<EulaDialogData>(MAT_DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly eulaService = inject(EulaService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private styleUtilService = inject(StyleUtilService);
  private window = inject(WINDOW);
  private userDataService = inject(UserDataService);
  private userSessionApiService = inject(UserSessionApiService);
  private elementRef = inject(ElementRef<HTMLElement>);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    const decoded = this.decodeEscapedHtml(this.data.eulaContent);
    const normalized = this.normalizeHtml(decoded);

    this.sanitizedContent = this.sanitizer.bypassSecurityTrustHtml(normalized);

    this.title = this.data.eulaTitle;
    this.eulaId = this.data.eulaId;
    this.eulaVersion = this.data.eulaVersion;
    this.usePostMethod = this.data.usePost;
    this.reviewOnly = !!this.data.reviewOnly;
    // If reviewOnly, get acceptedAt from API response
    if (this.reviewOnly && this.data.acceptedAt) {
      this.acceptedAt = this.data.acceptedAt;
    }
  }

  onCloseReview(): void {
    this.dialogRef.close(false);
  }

  ngAfterViewInit(): void {
    if (this.eulaContentRef?.nativeElement) {
      this.setupScrollTracking();
    }
  }

  ngAfterViewChecked(): void {
    // Apply notification styles only once when the element is available
    if (this.reviewOnly && this.acceptedAt && !this.notificationStylesApplied) {
      const shadowRoot = this.elementRef.nativeElement.shadowRoot;
      const notificationEl = shadowRoot?.querySelector(
        'core-notification[data-testid="eula-success-notification"]'
      ) as HTMLElement | null;

      if (notificationEl) {
        this.styleUtilService.setCoreNotificationContainerStyles(notificationEl, {
          paddingBlock: '12px',
          paddingLeft: '24px',
          minHeight: '50px',
          width: '100%'
        });
        this.notificationStylesApplied = true;
      }
    }
  }

  onAccept(): void {
    this.isSavingEula = true;
    const methodToBeUsed = this.usePostMethod ? 'saveEulaAcceptance' : 'saveEulaAcceptanceFromGet';
    this.eulaService[methodToBeUsed](this.eulaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSavingEula = false))
      )
      .subscribe({
        next: (response) => {
          if (response.acceptedAt) {
            this.eulaService.storeEulaAcceptance(true);
            this.eulaService.setShowSuccessNotification(true);
            this.eulaService.setEulaVersion(this.eulaVersion);
          }
          this.dialogRef.close(true);
        },
        error: () => {
          // Error handling - finalize will set isSavingEula to false
        }
      });
  }

  onExitApplication(): void {
    this.dialogRef.close(false);
    const returnUrl = this.window.location.href;
    this.userDataService.clearUserData();
    this.signedOut.emit();
    this.userSessionApiService.logout(returnUrl);
  }

  get isAcceptButtonEnabled(): boolean {
    return this.hasReadFullDocument && !this.isSavingEula;
  }

  private decodeEscapedHtml(html: string): string {
    if (!html) return '';

    const decoded = html.replace(/\\n/g, '\n').replace(/\\"/g, '"');

    const parser = document.createElement('textarea');
    parser.innerHTML = decoded;
    return parser.value.trim();
  }

  private normalizeHtml(html: string): string {
    if (!html) return '';

    let decoded = html.replace(/\uFFFD/g, '').replace(/\\n/g, '\n');

    if (!decoded.includes('<meta charset="UTF-8">')) {
      decoded = decoded.replace(/<head>/i, '<head><meta charset="UTF-8">');
    }

    return decoded;
  }

  private setupScrollTracking(): void {
    const contentElement = this.eulaContentRef.nativeElement;
    // If content fits without scrolling, mark as read
    if (contentElement.scrollHeight <= contentElement.clientHeight) {
      this.hasReadFullDocument = true;
      return;
    }
    fromEvent(contentElement, 'scroll')
      .pipe(throttleTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkScrollPosition();
      });
    this.checkScrollPosition();
  }

  private checkScrollPosition(): void {
    const element = this.eulaContentRef.nativeElement;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    const scrollThreshold = 50;
    const canScroll = scrollHeight > clientHeight;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - scrollThreshold;
    const hasScrolled = !canScroll || scrollTop > 0;

    if (isAtBottom && hasScrolled && !this.hasReadFullDocument) {
      this.hasReadFullDocument = true;
    }
  }
}
